import { Link } from "react-router-dom";
import TRABadge from "../primitives/TRABadge";
import styles from "./matchCard.module.css";
import { cn } from "../../utils/cn";
import { resolveSportModule } from "../../../sports/registry";
import { normalizeSportId } from "../../../domain/sport/sportTypes";

/**
 * Canonical match history row card (Design Language v1.0).
 * Player Intelligence: sport · arena · date · quick stats.
 * @param {{ card: object; className?: string; flat?: boolean }} props
 */
export default function TRAMatchCard({ card, className, flat = false }) {
  if (!card?.matchId) return null;

  const detailPath = `/history/match/${encodeURIComponent(card.matchId)}`;
  const sportLabel =
    card.sportLabel ||
    (card.sportId
      ? resolveSportModule(normalizeSportId(card.sportId))?.displayName
      : null);
  const metaType = (card.matchTypeUpper || card.matchTypeLabel || "MATCH").toString();
  const time = card.completedTimeLabel || card.completedAtLabel || "";
  const roomLabel = (
    card.arenaLabel ||
    card.sessionRoomLabel ||
    card.sessionRoomId ||
    ""
  )
    .toString()
    .trim();
  const metaLine = [sportLabel, roomLabel, metaType, time].filter(Boolean).join(" · ");

  const setColumns = card.setColumns?.length
    ? card.setColumns
    : [{ my: "—", opp: "—", iWonSet: card.isWin }];

  const ariaTeams = `${card.myTeamLine || "You"} vs ${card.opponentsTeamLine || "Opponents"}`;
  const quick = Array.isArray(card.quickStats) ? card.quickStats.slice(0, 4) : [];

  return (
    <Link
      to={detailPath}
      className={cn(styles.card, flat && styles.cardFlat, className)}
      aria-label={`${card.isWin ? "Won" : "Lost"}, ${ariaTeams}, ${card.finalScore || ""}`}
    >
      <div className={styles.top}>
        <span className={styles.meta}>{metaLine}</span>
        <TRABadge tone={card.isWin ? "success" : "danger"}>{card.isWin ? "WON" : "LOST"}</TRABadge>
      </div>
      <div className={styles.body}>
        <div className={styles.teams}>
          <div className={styles.teamRow}>
            <span className={styles.dotYou} aria-hidden />
            <span className={styles.teamNames}>{card.myTeamLine || "You"}</span>
          </div>
          <div className={styles.teamRow}>
            <span className={styles.dotOpp} aria-hidden />
            <span className={styles.teamNames}>{card.opponentsTeamLine || "Opponents"}</span>
          </div>
        </div>
        <div className={styles.scores} aria-label="Set scores">
          {setColumns.map((col, i) => (
            <div key={`set-${i}`} className={styles.setCol}>
              <span className={cn(styles.scoreMy, !col.iWonSet && styles.scoreDim)}>{col.my}</span>
              <span className={cn(styles.scoreOpp, col.iWonSet && styles.scoreDim)}>{col.opp}</span>
            </div>
          ))}
        </div>
      </div>
      {quick.length ? (
        <div className={styles.quickStrip} aria-label="Quick statistics">
          {quick.map((q) => (
            <div key={q.label} className={styles.quickCell}>
              <span className={styles.quickLabel}>{q.label}</span>
              <span className={styles.quickValue}>{q.value}</span>
            </div>
          ))}
        </div>
      ) : null}
    </Link>
  );
}
